/*
 * @Author: luting
 * @Date: 2022-05-07 09:05:04
 * @lastEditors: luting
 * @LastEditTime: 2023-06-15 15:59:36
 */
import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Form, Row, Col, Input } from 'sup-ui';
import { FormComponentProps } from 'sup-ui/lib/form';

import { getNetworkApi, updateNetworkApi } from 'root/services/netWork';
import { INetworkCardList, IRecordData, INetworkCardParams } from './types';

import SupTable from 'sup-rc-table';
import Modal from 'root/components/CommonModal';
import messages from './messages';

import styles from './index.less';

const formItemLayout = {
  labelCol: {
    span: 4,
  },
  wrapperCol: {
    span: 20,
  },
};

interface INetworkCard extends FormComponentProps {
  disabled?: boolean;
  hidden?: boolean;
  singleNodeId: string;
}
const { confirm } = Modal;

const flag = /^((25[0-5]|2[0-4]\d|((1\d{2})|([1-9]?\d)))\.){3}(25[0-5]|2[0-4]\d|((1\d{2})|([1-9]?\d)))$/;
/* newWorkCard中的table的高度得为100%才可以,这个组件在别的地方要用,该组件的样式height为100%,因此高度的限制都在父组件上进行 */
const NetworkCard: React.FC<INetworkCard> = (props) => {
  const intl = useIntl();
  const { form, disabled, hidden, singleNodeId } = props;
  const { getFieldDecorator } = form;
  const [dataSource, setDataSource] = useState<INetworkCardList[]>([]);
  const [visible, setVisible] = useState(false);
  const saveData: any = useRef({
    current: {
      cannotEdit: disabled,
    },
  });
  const [record, setRecord] = useState<IRecordData | null>(null);

  useEffect(() => {
    saveData.current.cannotEdit = disabled;
  }, [disabled]);

  const columns = [
    {
      title: intl.formatMessage(messages.name),
      dataIndex: 'name',
      width: 160,
    },
    {
      title: intl.formatMessage(messages.address),
      dataIndex: 'ipAddress',
      width: 160,
    },
    {
      title: intl.formatMessage(messages.gateway),
      dataIndex: 'gateway',
      width: 160,
    },
    {
      title: intl.formatMessage(messages.DNS),
      dataIndex: 'dns',
      width: 160,
      render: (text: string) => <span>{text || intl.formatMessage(messages.none)}</span>,
    },
    {
      title: intl.formatMessage(messages.operate),
      dataIndex: 'operate',
      width: 160,
      hide: hidden,
      align: 'center',
      render: (text: string, record: INetworkCardList) => {
        return record.allowModify ? (
          <div className={`${styles.edit}`} onClick={() => editEvent(record)}>
            {intl.formatMessage(messages.edit)}
          </div>
        ) : (
          <span className={styles.ipTip}> {intl.formatMessage(messages.tip)}</span>
        );
      },
    },
  ];

  useEffect(() => {
    getNetworkList();
  }, [singleNodeId]);

  // 获取网卡列表信息
  const getNetworkList = () => {
    if (!singleNodeId) return;
    getNetworkApi({ clientIds: [singleNodeId], labels: [] }).then((res) => {
      const { data = [] } = res;
      setDataSource(data);
    });
  };

  // 更新事件
  const onOk = (e: any) => {
    e.preventDefault();
    form.validateFields((err, values) => {
      if (!err) {
        const { gateway, ipAddress, netmask, dns, port } = values;
        const tempArr: string[] = [];
        if (dns) {
          tempArr.push(dns);
        }
        // 页面控制:没有dns不允许输入port
        if (port) {
          tempArr.push(port);
        }
        const params: INetworkCardParams = {
          name: record?.name as string,
          netmask,
          ipAddress,
          gateway,
          dns: tempArr,
          clientIds: [singleNodeId],
        };
        // 判断修改了IP和子掩码时候需要弹确认框
        const curIP = `${ipAddress}/${netmask}`;
        if (curIP !== dataSource[0]?.ipAddress) {
          confirm({
            title: intl.formatMessage(messages.confirmTip, { data: curIP }),
            onOk: () => {
              updateNetwork(params);
            },
          });
          return;
        }
        updateNetwork(params);
      }
    });
  };
  // 修改网卡
  const updateNetwork = (params: any) => {
    updateNetworkApi(params).then(() => {
      setVisible(false);
      setRecord(null);
      getNetworkList(); // 更新数据
    });
  };

  const editEvent = (record: INetworkCardList) => {
    setVisible(true);
    const { ipAddress = '', dns = '', gateway, name } = record;
    const tempData = {
      name,
      gateway,
      ipAddress: ipAddress.indexOf('/') > -1 ? ipAddress.substring(0, ipAddress.indexOf('/')) : ipAddress,
      netmask:
        ipAddress.indexOf('/') > -1 ? ipAddress.substring(ipAddress.indexOf('/') + 1, ipAddress.length) : undefined,
      dns: dns.indexOf(',') > -1 ? dns.substring(0, dns.indexOf(',')) : dns, // 如果没有找到","的话dns就是默认的第一项
      port: dns.indexOf(',') > -1 ? dns.substring(dns.indexOf(',') + 1, dns.length) : undefined, // 第二项就是undefined
    };
    setRecord(tempData);
  };

  // 自定义检查子掩码

  const checkValue = async (rule: any, value: string, callback: () => void) => {
    if (!value) return callback();

    const numberFlag = /^[1-9]\d*$/;

    if (numberFlag.test(value) && Number(value) >= 1 && Number(value) <= 31) {
      return callback();
    } else {
      throw new Error(intl.formatMessage(messages.rules));
    }
  };

  // 自定义检查dns
  const checkDns = async (rule: any, value: string, callback: () => void) => {
    // 截取第一段,不可以是127开头的ip
    if (!value) return; // 如果用户只是高亮没有输入值则不检查
    const firstNumber = value.substring(0, value.indexOf('.'));

    if (flag.test(value) && firstNumber !== '127') {
      return callback();
    } else {
      throw new Error(intl.formatMessage(messages.rules));
    }
  };

  return (
    <div className={styles.networkContent}>
      <div className={styles.title}>{intl.formatMessage(messages.networkCard)}</div>
      <SupTable
        autoWidth={true}
        showSelection={false}
        showSearchIcon={false}
        showColumnsFilter={false}
        columns={columns}
        dataSource={dataSource}
        empty={
          <div className={styles.emptyTable}>
            <span>{intl.formatMessage(messages.emptyText)}</span>
          </div>
        }
        pagination={null}
        rowKey="name"
      />

      <Modal
        visible={visible}
        title={intl.formatMessage(messages.updateNetwork)}
        onCancel={() => {
          setVisible(false);
        }}
        height="auto"
        onOk={onOk}
        className={styles.netWorkModal}
        destroyOnClose
      >
        <Form {...formItemLayout}>
          <Form.Item className={styles.addStar} label={intl.formatMessage(messages.address)} colon={false}>
            <Row gutter={10}>
              <Col span={16}>
                <Form.Item>
                  {getFieldDecorator('ipAddress', {
                    validateTrigger: ['onBlur'],
                    initialValue: record?.ipAddress,
                    rules: [
                      {
                        required: true,
                        message: intl.formatMessage(messages.placeholder) + intl.formatMessage(messages.address),
                      },
                      {
                        pattern: new RegExp(flag),
                        message: intl.formatMessage(messages.rules),
                      },
                    ],
                  })(<Input addonBefore="IP" disabled={saveData.current.cannotEdit} maxLength={64} />)}
                </Form.Item>
                {saveData.current.cannotEdit ? (
                  <span className={styles.ipTip}>{intl.formatMessage(messages.pleaseStopSupos)}</span>
                ) : null}
              </Col>

              <Col span={8}>
                <Form.Item>
                  {getFieldDecorator('netmask', {
                    validateTrigger: ['onBlur'],
                    initialValue: record?.netmask,
                    rules: [
                      {
                        required: true,
                        message: intl.formatMessage(messages.placeholder) + intl.formatMessage(messages.subMask),
                      },
                      {
                        validator: checkValue,
                      },
                    ],
                  })(
                    <Input
                      addonBefore={intl.formatMessage(messages.subMask)}
                      disabled={saveData.current.cannotEdit}
                      maxLength={64}
                    />,
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>

          <Form.Item label={intl.formatMessage(messages.gateway)} colon={false}>
            {getFieldDecorator('gateway', {
              validateTrigger: ['onBlur'],
              initialValue: record?.gateway,
              rules: [
                {
                  pattern: new RegExp(flag),
                  message: intl.formatMessage(messages.rules),
                },
              ],
            })(<Input maxLength={64} />)}
          </Form.Item>

          <Form.Item label={intl.formatMessage(messages.DNS)} colon={false}>
            <Form.Item>
              {getFieldDecorator('dns', {
                initialValue: record?.dns,
                validateTrigger: ['onBlur'],
                rules: [
                  {
                    validator: checkDns,
                  },
                ],
              })(<Input maxLength={64} />)}
            </Form.Item>
            <Form.Item>
              {getFieldDecorator('port', {
                initialValue: record?.port,
                validateTrigger: ['onBlur'],
                rules: [
                  {
                    validator: checkDns,
                  },
                ],
              })(<Input maxLength={64} />)}
            </Form.Item>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Form.create()(NetworkCard);
